<template>
  <div class="tasks-breadcrumb">
    <!-- Expanded: full breadcrumb for landscape / wide screens -->
    <div v-if="!isCollapsed" class="breadcrumb-full">
      <template v-for="(item, index) in breadcrumbItems">
        <span :key="'sep-' + index" v-if="index > 0" class="bc-sep"
          >&nbsp;&gt;</span
        >
        <b-tooltip
          :label="item.tooltip || item.label"
          position="is-bottom"
          :triggers="['hover', 'click']"
          :multilined="true"
        >
          <span
            :class="[
              'bc-item',
              {
                'bc-current': item.isCurrent,
                'bc-link': item.isClickable || item.isProductList,
                'bc-product-list': item.isProductList,
              },
            ]"
            @click="item.onClick && item.onClick()"
            >{{ item.label }}</span
          >
        </b-tooltip>
      </template>
    </div>

    <div v-else class="breadcrumb-collapsed">
      <b-dropdown
        v-if="hiddenItems.length"
        position="is-bottom-right"
        :close-on-click="true"
        :mobile-modal="false"
        :append-to-body="true"
      >
        <template #trigger>
          <div class="bc-dots"><b-icon icon="dots-horizontal" /></div>
        </template>
        <b-dropdown-item>
          <div
            class="bc-dropdown-path"
            v-for="(item, i) in hiddenItems"
            :key="i"
          >
            <span
              :class="{ 'bc-link': item.isClickable || item.isProductList }"
              @click="item.onClick && item.onClick()"
              >{{ item.label }}</span
            >
          </div>
        </b-dropdown-item>
      </b-dropdown>

      <span v-if="hiddenItems.length" class="bc-sep">&nbsp;&gt;</span>
      <span class="bc-item bc-current">{{
        currentItem && currentItem.label
      }}</span>
    </div>
  </div>
</template>

<script>
import FoldersManagerMixin from '../../mixins/FoldersManagerMixin';

export default {
  name: 'tasks-breadcrumb',
  mixins: [FoldersManagerMixin],
  props: {
    isTask: {
      type: Boolean,
      default: false,
    },
    tool: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      oilUser: '',
      frameTypesNamesFormatted: [],
      breadcrumbPublisher: '',
      breadcrumbEditor: '',
      labelTooltip: '',
      windowWidth: window.innerWidth,
      isPortrait: window.matchMedia('(orientation: portrait)').matches,
    };
  },
  computed: {
    task() {
      return this.$store.state.task;
    },
    user() {
      return this.$store.state.currentUser;
    },
    currentJob() {
      return this.$store.state.currentJob;
    },
    isExternalUser() {
      return !!(
        this.$route.params.token ||
        (this.user && this.user._id === this.oilUser)
      );
    },
    productListText() {
      return this.$route.name && this.$route.name.includes('editor')
        ? this.$t('product_template_list')
        : this.$t('work_order_list');
    },
    isCollapsed() {
      return this.isPortrait && this.windowWidth <= 1024;
    },
    breadcrumbItems() {
      const items = [];

      if (!this.isExternalUser) {
        items.push({
          label: this.productListText,
          isProductList: true,
          onClick: this.gotoProductList,
        });
      }

      const jobLabel = this.tool.includes('editor')
        ? this.labelTooltip
        : this.breadcrumbPublisher;

      if (jobLabel) {
        items.push({
          label: jobLabel,
          tooltip: this.labelTooltip,
          isClickable: !!(this.folderName || this.isTask),
          onClick: () =>
            this.task && this.task.folderId
              ? this.gotoTaskList(true)
              : this.gotoTaskList(),
        });
      }

      if (this.folderName) {
        items.push({
          label: this.folderName,
          isClickable: !!(this.folderName && this.subfolderName),
          onClick: () => this.gotoTaskList(false, true),
        });
      }

      if (this.subfolderName) {
        items.push({
          label: this.subfolderName,
          isClickable: false,
        });
      }

      if (this.isTask && this.task && this.task.taskName) {
        items.push({
          label: this.task.taskName,
          isCurrent: true,
        });
      } else if (items.length > 0) {
        items[items.length - 1] = {
          ...items[items.length - 1],
          isCurrent: true,
        };
      }

      return items;
    },
    currentItem() {
      return this.breadcrumbItems[this.breadcrumbItems.length - 1] || null;
    },
    hiddenItems() {
      return this.breadcrumbItems.slice(0, this.breadcrumbItems.length - 1);
    },
  },
  async mounted() {
    this.oilUser = process.env.VUE_APP_OIL_USER_EMAIL;

    if (this.currentJob) {
      this.getAndFormatFrameTypes();
      this.breadcrumbsPublisher();
      this.breadcrumbsEditor();
      this.fillLabelTooltip();
    }

    this.$store.watch(
      () => this.$store.state.currentJob,
      () => {
        this.getAndFormatFrameTypes();
        this.breadcrumbsPublisher();
        this.breadcrumbsEditor();
        this.fillLabelTooltip();
      },
      { deep: true }
    );

    this._mq = window.matchMedia('(orientation: portrait)');
    this._mqHandler = (e) => {
      this.isPortrait = e.matches;
    };
    this._mq.addEventListener('change', this._mqHandler);
    window.addEventListener('resize', this.handleResize);
  },
  beforeDestroy() {
    if (this._mq) {
      this._mq.removeEventListener('change', this._mqHandler);
    }
    window.removeEventListener('resize', this.handleResize);
  },
  methods: {
    handleResize() {
      this.windowWidth = window.innerWidth;
    },
    getAndFormatFrameTypes() {
      const job = this.currentJob;
      if (!job || typeof job.frameType === 'undefined' || !job.frameType)
        return '';
      if (typeof job.frameType !== 'string' && job.frameType.length > 1) {
        this.frameTypesNamesFormatted = [];
        job.frameType.forEach((frameType, index) => {
          this.frameTypesNamesFormatted.push(
            index !== 0 ? ` / ${frameType.text}` : frameType.text
          );
        });
      } else if (typeof job.frameType === 'string') {
        this.frameTypesNamesFormatted = job.frameType;
      } else {
        this.frameTypesNamesFormatted = job.frameType[0].text;
      }
    },
    breadcrumbsPublisher() {
      const job = this.currentJob;
      const parts = [
        job?.title,
        job?.customerInfoText,
        job?.projectInfoText,
        job?.serialNumber,
        job?.workOrderNumber,
      ].filter(Boolean);
      this.breadcrumbPublisher = parts.join(' / ');
    },
    breadcrumbsEditor() {
      const job = this.currentJob;
      const parts = [
        job?.title,
        this.frameTypesNamesFormatted,
        job?.userEnteredPartNumber,
        job?.documentNumber,
        job?.revision,
      ].filter(Boolean);
      this.breadcrumbEditor = parts.join(' / ');
    },
    fillLabelTooltip() {
      if (!this.tool.includes('editor')) {
        this.labelTooltip = this.breadcrumbPublisher;
      } else {
        this.labelTooltip = this.breadcrumbEditor;
      }
    },
    gotoProductList() {
      if (this.tool.includes('editor')) {
        this.$router.push({ name: 'editor-list' });
      } else if (this.tool.includes('inspector')) {
        this.$router.push({ name: 'inspector-list' });
      } else if (this.tool.includes('publisher')) {
        this.$router.push({ name: 'publisher-list' });
      } else {
        this.$router.push({ name: 'report-list' });
      }
    },
    gotoTaskList(task = false, folder = false) {
      if (!this.folderName && !this.isTask) return false;

      const { id } = this.$route.params;
      const { token } = this.$route.query || {};

      if (folder && !this.isTask) {
        const path = `/view/${this.tool}/${id}/folder/${this.folderId}`;
        return this.$router.push({
          path,
          query: token ? { token } : undefined,
        });
      }

      if (!task && this.task && this.task.folderId) {
        const path = `/view/${this.tool}/${id}/folder/${this.task.folderId}`;
        return this.$router.push({
          path,
          query: token ? { token } : undefined,
        });
      }

      if (this.subfolderId) {
        const path = `/view/${this.tool}/${id}/folder/${this.folderId}`;
        return this.$router.push({
          path,
          query: token ? { token } : undefined,
        });
      }

      this.$store.commit('SET_TASK_OBJECT', {});
      this.$router.push({
        name: `${this.tool}-tasks`,
        params: { id },
        query: token ? { token } : undefined,
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.tasks-breadcrumb {
  flex: 1 1 auto;
  display: inline-flex;
  align-items: center;
  overflow: hidden;
  min-width: 0;
}

.breadcrumb-full {
  display: inline-flex;
  align-items: center;
  overflow: hidden;
  min-width: 0;
}

.bc-sep {
  margin-right: 5px;
}

.bc-item {
  font-size: 18px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-height: 50px;
  margin: auto 0;
  color: #999;
}

.bc-current {
  font-weight: bold;
  color: #333;
}

.bc-link {
  cursor: pointer;
  &:hover {
    color: #555;
  }
}

.bc-product-list {
  min-width: 90px;
  flex-shrink: 0;
}

// Collapsed mode
.breadcrumb-collapsed {
  display: inline-flex;
  align-items: center;
  overflow: hidden;
  min-width: 0;
}

.bc-dots {
  cursor: pointer;
  margin-top: 15px;
  margin-right: 17px;
  &:hover {
    color: gray;
  }
}

.bc-dropdown-path {
  padding: 8px 12px;
  font-size: 16px;
  max-width: 280px;
  white-space: normal;
  word-break: break-word;
  line-height: 1.6;
  color: #999;
}
</style>
